export interface Permission {
  name: string;
  description: string;
  category: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  is_system_role: boolean;
}

export const PERMISSION_CATEGORIES = {
  PRODUCTS: 'Products',
  MANUFACTURING: 'Manufacturing',
  PURCHASE_ORDERS: 'Purchase Orders',
  SALES_ORDERS: 'Sales Orders',
  INVENTORY: 'Inventory',
  CATEGORIES: 'Categories',
  SUPPLIERS: 'Suppliers',
  LOCATIONS: 'Locations',
  RD: 'Research & Development',
  TOOLS: 'Tools',
  WARRANTIES: 'Warranties',
  REPORTS: 'Reports',
  SETTINGS: 'Settings',
  USER_MANAGEMENT: 'User Management',
  AUDIT: 'Audit',
} as const;

export const PERMISSIONS = {
  PRODUCTS_VIEW: 'products.view',
  PRODUCTS_CREATE: 'products.create',
  PRODUCTS_EDIT: 'products.edit',
  PRODUCTS_DELETE: 'products.delete',

  MANUFACTURING_VIEW: 'manufacturing.view',
  MANUFACTURING_CREATE: 'manufacturing.create',
  MANUFACTURING_EDIT: 'manufacturing.edit',
  MANUFACTURING_DELETE: 'manufacturing.delete',

  PURCHASE_ORDERS_VIEW: 'purchase_orders.view',
  PURCHASE_ORDERS_CREATE: 'purchase_orders.create',
  PURCHASE_ORDERS_EDIT: 'purchase_orders.edit',
  PURCHASE_ORDERS_DELETE: 'purchase_orders.delete',

  SALES_ORDERS_VIEW: 'sales_orders.view',
  SALES_ORDERS_CREATE: 'sales_orders.create',
  SALES_ORDERS_EDIT: 'sales_orders.edit',
  SALES_ORDERS_DELETE: 'sales_orders.delete',

  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_ADJUST: 'inventory.adjust',
  INVENTORY_TRANSFER: 'inventory.transfer',

  CATEGORIES_VIEW: 'categories.view',
  CATEGORIES_MANAGE: 'categories.manage',

  SUPPLIERS_VIEW: 'suppliers.view',
  SUPPLIERS_MANAGE: 'suppliers.manage',

  LOCATIONS_VIEW: 'locations.view',
  LOCATIONS_MANAGE: 'locations.manage',

  RD_VIEW: 'rd.view',
  RD_MANAGE: 'rd.manage',

  TOOLS_VIEW: 'tools.view',
  TOOLS_MANAGE: 'tools.manage',

  WARRANTIES_VIEW: 'warranties.view',
  WARRANTIES_MANAGE: 'warranties.manage',

  REPORTS_VIEW: 'reports.view',
  REPORTS_EXPORT: 'reports.export',

  SETTINGS_VIEW: 'settings.view',
  SETTINGS_MANAGE: 'settings.manage',

  USERS_VIEW: 'users.view',
  USERS_INVITE: 'users.invite',
  USERS_MANAGE: 'users.manage',
  USERS_DELETE: 'users.delete',

  AUDIT_VIEW: 'audit.view',
} as const;

export type PermissionName = typeof PERMISSIONS[keyof typeof PERMISSIONS];

export const ROLE_NAMES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  STAFF: 'Staff',
  VIEWER: 'Viewer',
} as const;

export const hasPermission = (
  userPermissions: string[],
  requiredPermission: PermissionName | PermissionName[]
): boolean => {
  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some(p => userPermissions.includes(p));
  }
  return userPermissions.includes(requiredPermission);
};

export const hasAllPermissions = (
  userPermissions: string[],
  requiredPermissions: PermissionName[]
): boolean => {
  return requiredPermissions.every(p => userPermissions.includes(p));
};
